import React from 'react'
import { format, isPast, isToday } from 'date-fns'
import { Calendar, Paperclip, MessageSquare, ExternalLink, ListChecks, Zap } from 'lucide-react'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { cn } from '@/lib/utils'
import PriorityBadge from './PriorityBadge'
import TaskDisplayId from './TaskDisplayId'
import TaskTimerButton from './TaskTimerButton'
import { LabelChip } from './LabelsInput'

const TaskCard = ({ task, onClick, dragHandleProps = {}, isDragging = false, projectKey }) => {
  const displayId = projectKey && task.task_number ? `${projectKey}-${task.task_number}` : null
  const assigneeName = task.assignee?.employee
    ? `${task.assignee.employee.first_name} ${task.assignee.employee.last_name}`
    : task.assignee?.email || null
  const assigneeInitial = assigneeName?.charAt(0).toUpperCase()

  const dueDate = task.due_date ? new Date(task.due_date + 'T00:00:00') : null
  const dueDateOverdue = dueDate && isPast(dueDate) && !isToday(dueDate) && task.status?.category !== 'done'
  const dueDateToday = dueDate && isToday(dueDate)

  return (
    <div
      onClick={onClick}
      className={cn(
        'bg-background border rounded-lg p-3 cursor-pointer hover:shadow-sm transition-shadow space-y-2 select-none',
        isDragging && 'shadow-lg ring-2 ring-primary/20 rotate-1'
      )}
      {...dragHandleProps}
    >
      {/* Display ID + Priority (top row) */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          {displayId ? (
            <TaskDisplayId projectId={task.project_id} projectKey={projectKey} taskId={task.id} taskNumber={task.task_number} taskType={task.type} />
          ) : <div />}
          {task.external_key && (
            <a
              href={task.external_url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="flex items-center gap-0.5 text-[10px] text-blue-600 hover:text-blue-800 bg-blue-50 dark:bg-blue-950/30 px-1.5 py-0.5 rounded font-medium"
              title={`View in external system: ${task.external_key}`}
            >
              <ExternalLink className="h-2.5 w-2.5" />
              {task.external_key}
            </a>
          )}
        </div>
        <PriorityBadge priority={task.priority} showLabel={false} />
      </div>

      {/* Title */}
      <p className="text-sm font-medium leading-snug line-clamp-2">{task.title}</p>

      {/* Epic + labels */}
      {(task.parent?.type?.hierarchy_level === 1 || task.labels?.length > 0) && (
        <div className="flex items-center gap-1 flex-wrap">
          {task.parent?.type?.hierarchy_level === 1 && (
            <span
              className="inline-flex items-center gap-0.5 rounded px-1.5 py-0.5 text-[10px] font-medium bg-violet-100 text-violet-800 dark:bg-violet-950 dark:text-violet-200 max-w-[160px]"
              title={`Epic: ${task.parent.title}`}
            >
              <Zap className="h-2.5 w-2.5 shrink-0" />
              <span className="truncate">{task.parent.title}</span>
            </span>
          )}
          {(task.labels || []).slice(0, 3).map(label => <LabelChip key={label} label={label} className="text-[10px]" />)}
          {task.labels?.length > 3 && <span className="text-[10px] text-muted-foreground">+{task.labels.length - 3}</span>}
        </div>
      )}

      {/* Due date */}
      <div className="flex items-center gap-2 flex-wrap">
        {dueDate && (
          <span className={cn(
            'flex items-center gap-1 text-xs',
            dueDateOverdue ? 'text-destructive font-medium' :
            dueDateToday ? 'text-orange-600 font-medium' :
            'text-muted-foreground'
          )}>
            <Calendar className="h-3 w-3" />
            {format(dueDate, 'MMM d')}
          </span>
        )}
      </div>

      {/* Footer: assignee + metadata */}
      <div className="flex items-center justify-between">
        {assigneeName ? (
          <div className="flex items-center gap-1.5">
            <Avatar className="h-5 w-5">
              <AvatarFallback className="text-[10px] bg-primary/10 text-primary">{assigneeInitial}</AvatarFallback>
            </Avatar>
            <span className="text-xs text-muted-foreground truncate max-w-[100px]">{assigneeName}</span>
          </div>
        ) : <div />}

        <div className="flex items-center gap-2 text-muted-foreground">
          {task.status?.category !== 'done' && (
            <TaskTimerButton
              taskId={task.id}
              projectId={task.project_id}
              taskTitle={task.title}
            />
          )}
          {task.story_points !== null && task.story_points !== undefined && (
            <span className="rounded-full bg-muted px-1.5 text-[11px] tabular-nums text-foreground" title="Story points">{task.story_points}</span>
          )}
          {task.childCount > 0 && (
            <span className="flex items-center gap-0.5 text-xs" title={`${task.childDoneCount} of ${task.childCount} ${task.type?.hierarchy_level === 1 ? 'tasks' : 'sub-tasks'} done`}>
              <ListChecks className="h-3 w-3" />
              {task.childDoneCount}/{task.childCount}
            </span>
          )}
          {task.comments?.length > 0 && (
            <span className="flex items-center gap-0.5 text-xs">
              <MessageSquare className="h-3 w-3" />
              {task.comments.length}
            </span>
          )}
          {task.attachments?.length > 0 && (
            <span className="flex items-center gap-0.5 text-xs">
              <Paperclip className="h-3 w-3" />
              {task.attachments.length}
            </span>
          )}
        </div>
      </div>
    </div>
  )
}

export default TaskCard
